import type { Dispatch, SetStateAction } from 'react';
import { useContext, useEffect } from 'react';

import { getStrapSizeDatas, type StrapMapValue } from '@/utils/straps';

import { getIndexName, getProducts } from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { StrapHit } from '@/integrations/algolia/types';
import { NEXT_PUBLIC_DEFAULT_APP_LOCALE } from '@/lib/const';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { LocaleContext } from '@/providers/LocaleProvider';

export const useStrapSizeDatas = (
  setStrapSizeDatas: Dispatch<SetStateAction<StrapMapValue | undefined>>,
  sku?: string | null,
) => {
  const locale = useContext(LocaleContext);
  const draftMode = useContext(DraftModeContext);

  const algoliaSearchClient = createSearchClient({
    isPreview: draftMode,
  });

  useEffect(() => {
    const fetchStrapSized = async () => {
      if (!sku) return;
      const indexName = getIndexName(
        'PRODUCT',
        locale ?? NEXT_PUBLIC_DEFAULT_APP_LOCALE,
      );

      const strapHits = await getProducts<StrapHit>(
        locale,
        [sku],
        algoliaSearchClient,
      );
      const validStrapHits = strapHits?.filter(
        (hit): hit is StrapHit => hit !== null,
      );
      if (!validStrapHits?.length) return;

      const sizeDatas = await getStrapSizeDatas(
        validStrapHits,
        indexName,
        draftMode,
      );
      setStrapSizeDatas(sizeDatas[sku]);
    };

    fetchStrapSized();
  }, [sku]);
};
